import EditorActions from 'components/Common/EditorActions'
import CompetitionTabHeader from "../../CompetitionTabHeader"
import { Form, Formik } from "formik"
import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
  useMemo,
} from "react"
import SearchBox from "components/Common/SearchBox"
import { useHistory, useLocation } from "react-router-dom"
import { useToasts } from "react-toast-notifications"
import { connect } from "react-redux"
import moment from "moment-timezone"
import DrawPlayerDetails from "../../components/DrawPlayerDetails"
import ParticipantMatches from "./components/ParticipantMatches"
import MatchHighlights from "./components/MatchHighlights"
import DrawSection from "./components/DrawSection"
import ThirdPlaceMatch from "./components/ThirdPlaceMatch"
import useUniformMatchCards from "./useUniformMatchCards"
import QualificationStandings from "./components/QualificationStandings"
import "./mainDraw.scss"
import PhaseDrawActions from "../Phases/PhaseDrawActions"

import { store as socketStore } from "components/Common/Socket/store"
import ListenerComponent from "components/Common/Socket/ListenerComponent"

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Card,
  CardBody,
  FormGroup,
} from "reactstrap"

import { getMatches, getMainDraw } from "./actions"
import { matchSendFailure, updateMatchScore } from "./api"
import ScoreForm, { validateScoreValues, scoreDraftChanged } from "./components/ScoreForm"
import { getMainDrawXls } from "../../../../../api"
import { isTenis10Event } from "../../../../../tenis10Event"
import GameSheetsButtons from "./GameSheetsButtons"
import { isMatchContextChanged, matchWriteFailure, matchWriteSuccess } from "./matchWriteText"
import { matchFormatLine, matchFormatOf } from "../../../../../tenis10MatchFormat"

const NoMatches = () => (
  <Card>
    <CardBody>No matches yet</CardBody>
  </Card>
)

const MATCH_CONFIRMATION_MS = 30000
// How long the answer to a save no editor waits for any more is still expected.
const LATE_ANSWER_MS = 120000
const NO_CONFIRMATION =
  "No confirmation arrived. Reload the Main Draw to check whether the score was saved."

// The gateway answers a score save with 202; Accounts confirms or refuses it later on
// `matches-response`, which carries no request identity for match writes. The editor
// that sent the save waits for the next answer the shared draw state does not own,
// for MATCH_CONFIRMATION_MS at most. An answer no editor waits for is a toast.
// A save the gateway accepted but whose editor stopped waiting (closed, switched or
// timed out) still owes an answer. Answers arrive oldest first, so the next one is
// that save's: it is a toast too, never the answer of an editor that saved later.
// An owed answer not arrived within LATE_ANSWER_MS is presumed lost. Replace, swap and
// create are not counted (a create with BYE gets two answers), so one of their answers
// can still reach a waiting editor until the gateway forwards the request identity.
const useMatchConfirmation = () => {
  const waiting = useRef(null)
  const owed = useRef([])
  useEffect(() => () => waiting.current?.cancel(), [])
  const expectAnswer = useCallback(() => {
    waiting.current?.cancel()
    let settle
    let settled = false, accepted = false, abandoned = false
    const owe = () => owed.current.push(Date.now())
    const confirmation = { result: new Promise(resolve => { settle = resolve }) }
    const timer = setTimeout(
      () => confirmation.deliver({ timedOut: true }),
      MATCH_CONFIRMATION_MS
    )
    confirmation.deliver = outcome => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (waiting.current === confirmation) waiting.current = null
      if (outcome.cancelled || outcome.timedOut) {
        abandoned = true
        if (accepted) owe()
      }
      settle(outcome)
    }
    confirmation.cancel = () => confirmation.deliver({ cancelled: true })
    // The gateway accepted the save (202): Accounts will answer it.
    confirmation.accepted = () => {
      accepted = true
      if (abandoned) owe()
    }
    waiting.current = confirmation
    return confirmation
  }, [])
  const takeOwedAnswer = useCallback(() => {
    const since = Date.now() - LATE_ANSWER_MS
    owed.current = owed.current.filter(at => at >= since)
    return owed.current.shift() !== undefined
  }, [])
  return { waiting, expectAnswer, takeOwedAnswer }
}

const MatchesList = ({
  competitionDetails = {},
  matches = [],
  mainDraw = [],
  drawType,
  view,
  query,
  revealedMatchIds,
  modal,
  setModal,
  socketClientId,
  eventDetails,
  toolbar,
  expectAnswer,
}) => {
  const editorIdentity = useRef({ id: modal, active: true })
  if (editorIdentity.current.id !== modal)
    editorIdentity.current = { id: modal, active: true }
  // Closing or switching the editor drops its wait: Accounts' later answer becomes a toast.
  useEffect(() => {
    const identity = editorIdentity.current
    return () => {
      identity.active = false
      identity.confirmation?.cancel()
    }
  }, [modal])
  const [selectedMatch] = matches.filter(({ _id }) => _id === modal)

  const scheduleTimezone = moment.tz.zone(eventDetails?.timezone || "")
    ? eventDetails.timezone
    : "Europe/Bucharest"
  const formatScheduleValue = (value, format) =>
    value && moment(value).isValid()
      ? moment(value).tz(scheduleTimezone).format(format)
      : ""

  return (
    <>
      <DrawSection
        {...{
          mainDraw,
          matches,
          competitionDetails,
          eventDetails,
          setModal,
          drawType,
          view,
          query,
          revealedMatchIds,
          toolbar,
        }}
      />
      <Modal
        scrollable
        className="main-draw-edit-dialog"
        size="lg"
        isOpen={Boolean(modal)}
        toggle={() => setModal(false)}
      >
        <ModalHeader toggle={() => setModal(false)}>Edit match</ModalHeader>
        <Formik
          key={modal || "closed"}
          validate={values => validateScoreValues(values, selectedMatch)}
          initialValues={{
            player: {},
            opponent: {},
          }}
          onSubmit={async (values, { setStatus }) => {
            const identity = editorIdentity.current
            const open = () => identity === editorIdentity.current && identity.active
            setStatus(null)
            // Listen before sending: the answer can arrive before the 202.
            const confirmation = expectAnswer()
            identity.confirmation = confirmation
            try {
              await updateMatchScore(
                modal,
                competitionDetails,
                { player: values.player, opponent: values.opponent },
                socketClientId
              )
            } catch (error) {
              confirmation.cancel()
              if (open()) setStatus({ alert: matchSendFailure(error, "inputs are") })
              return
            }
            confirmation.accepted()
            if (!open()) return
            // Formik keeps isSubmitting until this returns, so the footer's waiting line covers the wait.
            const outcome = await confirmation.result
            // A confirmed save closes the editor from the page's listener.
            if (!open() || outcome.cancelled || outcome.success) return
            setStatus(
              outcome.timedOut
                ? { alert: NO_CONFIRMATION, tone: "warning" }
                : { alert: matchWriteFailure(outcome.message) }
            )
          }}
        >
          {({ setFieldValue, values, errors, status, isSubmitting }) => (
            <Form noValidate>
              <ModalBody>
                {status?.alert && (
                  <div role="alert" className={`alert alert-${status.tone || "danger"}`}>
                    {status.alert}
                  </div>
                )}
                <h6>Score</h6>
                {/* A Tenis10 match names the format its score must finish (RELEASE-TENIS10 § 11 P54 B1). */}
                {matchFormatOf(selectedMatch) ? (
                  <p className="text-muted small">
                    Format: <strong className="text-dark">{matchFormatLine(matchFormatOf(selectedMatch).key)}</strong>. Type the
                    final score; leave it blank if the match was not played.
                  </p>
                ) : (
                <p className="text-muted small">
                  Type the score directly. Use Tab to move between fields; leave
                  unplayed sets blank.
                </p>
                )}
                {errors.score && (
                  <div role="alert" className="alert alert-danger">
                    {errors.score}
                  </div>
                )}
                <ScoreForm
                  match={selectedMatch}
                  scoreValues={values}
                  setFieldValue={setFieldValue}
                />
                <h6 className="mt-3">Schedule</h6>
                <p id="match-schedule-help" className="text-muted small">
                  Schedule is managed in Match Planner. Times shown in{" "}
                  {scheduleTimezone}.
                </p>
                <Row className="main-draw-schedule-fields">
                  {[
                    [
                      "day",
                      "Day",
                      formatScheduleValue(
                        selectedMatch?.scheduler?.playingDay,
                        "DD MMM YYYY"
                      ),
                    ],
                    [
                      "time",
                      "Time",
                      formatScheduleValue(
                        selectedMatch?.scheduler?.playingTime,
                        "HH:mm"
                      ),
                    ],
                    ["court", "Court", selectedMatch?.scheduler?.court || ""],
                  ].map(([key, label, value]) => (
                    <Col key={key}>
                      <FormGroup>
                        <label
                          className="mb-1"
                          htmlFor={`match-schedule-${key}`}
                        >
                          {label}
                        </label>
                        <input
                          id={`match-schedule-${key}`}
                          className="form-control"
                          type="text"
                          readOnly
                          value={value}
                          placeholder="Not scheduled"
                          aria-describedby="match-schedule-help"
                        />
                      </FormGroup>
                    </Col>
                  ))}
                </Row>
              </ModalBody>
              <ModalFooter><EditorActions dirty={scoreDraftChanged(selectedMatch, values)} pending={isSubmitting} saveLabel="Update">
                <Button color="secondary" onClick={() => setModal(false)}>
                  Cancel
                </Button>
                {/* A Tenis10 score its format does not finish is held, as the server would refuse it. */}
                <Button type="submit" color="primary" disabled={isSubmitting || Boolean(matchFormatOf(selectedMatch) && errors.score)}>
                  Update
                </Button>
              </EditorActions></ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  )
}

const Matches = ({
  fetchMatches,
  fetchMainDraw,
  matches,
  mainDraw,
  dataCompetitionId,
  reads = {},
  drawOperation,
  drawReadRevision,
  competitionDetails,
  match: { params: { competitionId } = {} } = {},
  eventDetails,
}) => {
  const [modal, setModal] = useState(false)
  const [modalPhase, setModalPhase] = useState(false)
  const { addToast } = useToasts()
  const globalState = useContext(socketStore)
  const { state: { socket: { socketClientId } = {} } = {} } = globalState
  const [generatingXls, setGeneratingXls] = useState(false)

  const root = useRef(null)
  const location = useLocation(), history = useHistory()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])
  const revealedMatchIds = useMemo(() => params.getAll("match"), [params])
  const [query, setQuery] = useState("")
  const view = "responsive"

  const refresh = useCallback(() => {
    fetchMatches({ competitionId })
    fetchMainDraw(competitionId)
  }, [fetchMatches, fetchMainDraw, competitionId])
  useEffect(() => {
    setModal(false)
    setModalPhase(false)
    refresh()
  }, [refresh, drawReadRevision])
  useEffect(() => {
    const participant = params.get("participant")
    setQuery(participant && /^\d+$/.test(participant) ? `#${participant}` : "")
  }, [competitionId, params])
  const clearHighlights = () => {
    setQuery("")
    const next = new URLSearchParams(location.search)
    next.delete("match")
    next.delete("participant")
    history.replace({ ...location, search: next.toString() ? `?${next}` : "" })
  }
  const current = dataCompetitionId === competitionId
  const readError = current && (reads.matches?.error || reads.mainDraw?.error)
  // Once both reads have succeeded for this competition the draw stays mounted
  // across refreshes (score updates, replacements, phase actions), so the
  // operator keeps their scroll position while the new data arrives.
  const ready =
    current &&
    competitionDetails?._id === competitionId &&
    Boolean(reads.matches?.loaded && reads.mainDraw?.loaded)
  const refreshing =
    ready &&
    [reads.matches, reads.mainDraw].some(read => read?.status === "loading")

  useUniformMatchCards(root, mainDraw, ready)

  const { waiting, expectAnswer, takeOwedAnswer } = useMatchConfirmation()
  const listenForResponse = async result => {
    if (drawOperation?.owns(result)) return
    const { success, message } = result
    // A waiting score editor shows a refusal itself and closes on a confirmed save,
    // unless this answer is still owed to an earlier save nobody waits for.
    const editor = takeOwedAnswer() ? null : waiting.current
    editor?.deliver(result)
    if (!success) {
      // The match changed after the admin opened it: show the draw as it is now.
      if (isMatchContextChanged(message)) refresh()
      if (editor) return
      return addToast(matchWriteFailure(message), {
        appearance: "error",
        autoDismiss: true,
      })
    }
    refresh()
    if (editor) {
      setModal(false)
      setModalPhase(false)
    }
    return addToast(matchWriteSuccess(message), {
      appearance: "success",
      autoDismiss: true,
    })
  }

  let phaseDrawType
  if (mainDraw?.phases) {
    phaseDrawType = mainDraw?.phases?.[0]?.[0]?.competition?.drawType
  }

  return (
    <div
      className="competition-main-draw"
      ref={root}
      aria-busy={refreshing || undefined}
    >
      <DrawPlayerDetails mainDraw={ready ? mainDraw : undefined}>
      <CompetitionTabHeader title="Main Draw" />
      <ListenerComponent
        listenFunction={listenForResponse}
        topic="matches-response"
      />
      {readError && (
        <div role="alert" className="alert alert-danger">
          {ready
            ? "Main Draw could not be refreshed; showing the last loaded data."
            : "Main Draw could not be loaded."}{" "}
          <Button color="light" onClick={refresh}>
            Retry Main Draw
          </Button>
        </div>
      )}
      {!ready ? (
        !readError && <p role="status">Loading Main Draw…</p>
      ) : !mainDraw?.matches?.flat()?.length ? (
        <NoMatches />
      ) : (
        <>
          <MatchesList
            view={view}
            query={query}
            revealedMatchIds={revealedMatchIds}
            matches={matches}
            drawType={competitionDetails?.drawModel}
            mainDraw={mainDraw}
            eventDetails={eventDetails}
            // The primary draw owns the pinned toolbar: participant search on the
            // left, the section shortcuts, then the export action. It stays in view
            // while the operator scrolls the draw, for groups and eliminatory alike.
            toolbar={{
              search: (
                <div className="main-draw-search-and-highlights">
                <div className="main-draw-search-controls">
                  <label className="main-draw-search">
                    Find participant
                    <SearchBox
                      placeholder="Name or #ID"
                      value={query}
                      onValueChange={setQuery}
                      onClear={clearHighlights}
                    />
                  </label>
                </div>
                {/* While the field holds text its „×” clears the search and the highlights; the bar keeps its own
                    button for the highlights a link opened (?match=) */}
                <MatchHighlights root={root} data={mainDraw}
                  selection={[query.trim(), ...revealedMatchIds].filter(Boolean).join("|")}
                  autoRevealKey={params.has("match") || params.has("participant") ? `${competitionId}:${location.search}` : null}
                  onClear={query.trim() ? null : clearHighlights} clearLabel="Clear highlights" />
                </div>
              ),
              actions: (
                <>
                  {refreshing && (
                    <span role="status" className="main-draw-refreshing text-muted">
                      Updating Main Draw…
                    </span>
                  )}
                  {isTenis10Event(eventDetails) ? (
                    <GameSheetsButtons
                      eventId={eventDetails?._id}
                      competitionId={competitionId}
                      disabled={drawOperation?.locked}
                    />
                  ) : (
                    <Button
                      onClick={async () => {
                        await getMainDrawXls({
                          competitionId,
                          eventId: eventDetails?._id,
                          setGeneratingXls,
                          addToast,
                        })
                      }}
                      className="main-draw-export"
                      color="primary"
                      disabled={generatingXls || drawOperation?.locked}
                    >
                      {generatingXls ? "Generating .XLS..." : "Generate Main Draw .XLS"}
                    </Button>
                  )}
                </>
              ),
              results: (
                <ParticipantMatches
                  mainDraw={mainDraw}
                  drawType={competitionDetails.drawModel}
                  query={query}
                  eventDetails={eventDetails}
                  onEdit={(id, phase) => (phase ? setModalPhase(id) : setModal(id))}
                />
              ),
            }}
            {...{
              modal: modal,
              setModal: setModal,
              competitionDetails,
              socketClientId,
              expectAnswer,
            }}
          />

          <QualificationStandings
            gameType={competitionDetails.gameType}
            players={mainDraw?.allGroupPhasePlayers}
            phases={mainDraw?.phases}
          />

          {/* Competition Groups with Phases */}
          {mainDraw?.phases?.length > 0 && (
            <>
              <div className="flex justify-content-between">
                <h3>Final phase</h3>
              </div>
              <PhaseDrawActions
                eventId={eventDetails?._id}
                competitionId={competitionId}
                onSaved={() => refresh()}
              />

              <MatchesList
                view={view}
                query={query}
                revealedMatchIds={revealedMatchIds}
                matches={matches}
                drawType={phaseDrawType}
                mainDraw={mainDraw}
                eventDetails={eventDetails}
                {...{
                  modal: modalPhase,
                  setModal: setModalPhase,
                  competitionDetails,
                  socketClientId,
                  expectAnswer,
                }}
              />
              <ThirdPlaceMatch match={mainDraw?.thirdPlaceMatch} competitionDetails={competitionDetails} eventDetails={eventDetails}
                setModal={setModalPhase} matches={matches} />
            </>
          )}
        </>
      )}
      </DrawPlayerDetails>
    </div>
  )
}

const mapStateToProps = ({
  matches: {
    matches = [],
    mainDraw = {},
    competitionId: dataCompetitionId,
    reads,
  } = {},
  session: { userDetails } = {},
}) => ({
  matches,
  mainDraw,
  userDetails,
  dataCompetitionId,
  reads,
})

export default connect(mapStateToProps, {
  fetchMatches: getMatches,
  fetchMainDraw: getMainDraw,
})(Matches)
